/**
 * Timeline Page
 * 
 * Visual life timeline with events, phases, and wealth projections
 */

import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  addEvent,
  updateEvent,
  removeEvent,
  selectEvent,
  setViewRange,
  generateEventsFromLifePlan,
  generatePhasesFromLifePlan,
  visitPage,
} from '@/store'
import { PageHeader } from '@/components/layout'
import { TimelineView, EventDialog, WealthGraph } from '@/components/timeline'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { LifeEvent } from '@/types'
import { 
  ClipboardList, 
  Sparkles, 
  TrendingUp, 
  Target,
  DollarSign,
  Calendar,
  RefreshCw,
} from 'lucide-react'
import { generateWealthProjection, calculateRetirementReadiness } from '@/services/projectionEngine'

export default function Timeline() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  
  const lifePlan = useAppSelector((state) => state.lifePlan)
  const timeline = useAppSelector((state) => state.timeline)
  const questionnaire = useAppSelector((state) => state.questionnaire)
  
  const [eventDialogOpen, setEventDialogOpen] = React.useState(false)
  const [editingEvent, setEditingEvent] = React.useState<LifeEvent | undefined>()
  
  // Track page visit
  React.useEffect(() => {
    dispatch(visitPage('timeline'))
  }, [dispatch])
  
  // Auto-generate events and phases from life plan if empty
  React.useEffect(() => {
    if (timeline.events.length === 0 && lifePlan.personal.birthDate) {
      dispatch(generateEventsFromLifePlan({
        personal: lifePlan.personal,
        family: lifePlan.family,
        career: lifePlan.career,
        preferences: lifePlan.preferences,
        goals: lifePlan.goals,
      }))
    }
    
    if (timeline.phases.length === 0 && lifePlan.personal.birthDate) {
      dispatch(generatePhasesFromLifePlan({
        birthDate: lifePlan.personal.birthDate,
        retirementAge: lifePlan.personal.retirementAge,
        lifeExpectancy: lifePlan.personal.lifeExpectancy,
        currentIncome: lifePlan.computed.monthlyIncome * 12,
      }))
    }
  }, [dispatch, lifePlan, timeline.events.length, timeline.phases.length])
  
  // Calculate projections
  const projection = React.useMemo(() => {
    return generateWealthProjection({
      lifePlan,
      timeline,
    })
  }, [lifePlan, timeline])
  
  const retirementReadiness = React.useMemo(() => {
    return calculateRetirementReadiness(projection, lifePlan)
  }, [projection, lifePlan])
  
  // Get key stats
  const currentYear = new Date().getFullYear()
  const currentProjection = projection.points.find(p => p.year === currentYear)
  const retirementYear = lifePlan.personal.birthDate 
    ? new Date(lifePlan.personal.birthDate).getFullYear() + lifePlan.personal.retirementAge
    : currentYear + 30
  const retirementProjection = projection.points.find(p => p.year === retirementYear)
  
  // Handle event actions
  const handleAddEvent = () => {
    setEditingEvent(undefined)
    setEventDialogOpen(true)
  }
  
  const handleEventClick = (event: LifeEvent) => {
    dispatch(selectEvent(event.id))
    setEditingEvent(event)
    setEventDialogOpen(true)
  }
  
  const handleSaveEvent = (eventData: Omit<LifeEvent, 'id' | 'isPast' | 'isAutoGenerated'>) => {
    if (editingEvent?.id) {
      dispatch(updateEvent({ id: editingEvent.id, updates: eventData }))
    } else {
      dispatch(addEvent({
        ...eventData,
        isAutoGenerated: false,
      }))
    }
  }
  
  const handleDeleteEvent = () => {
    if (editingEvent?.id) {
      dispatch(removeEvent(editingEvent.id))
    }
  }
  
  const handleRegenerateTimeline = () => {
    dispatch(generateEventsFromLifePlan({
      personal: lifePlan.personal,
      family: lifePlan.family,
      career: lifePlan.career,
      preferences: lifePlan.preferences,
      goals: lifePlan.goals,
    }))
    dispatch(generatePhasesFromLifePlan({
      birthDate: lifePlan.personal.birthDate,
      retirementAge: lifePlan.personal.retirementAge,
      lifeExpectancy: lifePlan.personal.lifeExpectancy,
      currentIncome: lifePlan.computed.monthlyIncome * 12,
    }))
  }
  
  // Show onboarding if questionnaire not completed
  if (!questionnaire.isCompleted && !lifePlan.personal.birthDate) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ClipboardList className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Welcome to Your Life Plan</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
            To build your personalized financial timeline, we need to learn about your situation.
            This takes about 15-20 minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/questionnaire')}>
              <Sparkles className="mr-2 h-5 w-5" />
              Start Questionnaire
            </Button>
            <Button variant="outline" size="lg" onClick={handleAddEvent}>
              <Calendar className="mr-2 h-5 w-5" />
              Add Event Manually
            </Button>
          </div>
        </div>
        
        <EventDialog
          open={eventDialogOpen}
          onOpenChange={setEventDialogOpen}
          event={editingEvent}
          onSave={handleSaveEvent}
          onDelete={editingEvent ? handleDeleteEvent : undefined}
        />
      </div>
    )
  }
  
  return (
    <div className="container max-w-7xl py-6">
      <PageHeader
        title="Life Timeline"
        description="Visualize your financial journey from now through retirement and beyond"
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/questionnaire')}>
            <ClipboardList className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
          <Button variant="outline" onClick={handleRegenerateTimeline}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Regenerate
          </Button>
        </div>
      </PageHeader>
      
      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Current Net Worth</p>
                <p className="text-2xl font-bold">
                  ${(currentProjection?.netWorth || lifePlan.computed.netWorth).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">At Retirement</p>
                <p className="text-2xl font-bold">
                  ${(retirementProjection?.netWorth || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  in today's dollars
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Retirement Readiness</p>
                <p className="text-2xl font-bold">{retirementReadiness}%</p>
                <Badge variant={retirementReadiness >= 80 ? 'default' : retirementReadiness >= 60 ? 'secondary' : 'destructive'}>
                  {retirementReadiness >= 80 ? 'On Track' : retirementReadiness >= 60 ? 'Needs Attention' : 'At Risk'}
                </Badge>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-full">
                <Target className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Life Events</p>
                <p className="text-2xl font-bold">{timeline.events.length}</p>
                <p className="text-xs text-muted-foreground">
                  {timeline.events.filter(e => new Date(e.date) > new Date()).length} upcoming
                </p>
              </div>
              <div className="p-3 bg-violet-500/10 rounded-full">
                <Calendar className="h-6 w-6 text-violet-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Wealth Projection Graph */}
      <div className="mt-8">
        <WealthGraph
          projection={projection}
          events={timeline.events}
          currentYear={currentYear}
          retirementYear={retirementYear}
          onEventClick={(eventId) => {
            const event = timeline.events.find(e => e.id === eventId)
            if (event) handleEventClick(event)
          }}
        />
      </div>
      
      {/* Timeline Visualization */}
      <div className="mt-8">
        <TimelineView
          events={timeline.events}
          phases={timeline.phases}
          viewStartYear={timeline.viewStartYear}
          viewEndYear={timeline.viewEndYear}
          selectedEventId={timeline.selectedEventId}
          onEventClick={handleEventClick}
          onAddEvent={handleAddEvent}
          onYearRangeChange={(start, end) => dispatch(setViewRange({ startYear: start, endYear: end }))}
        />
      </div>
      
      {/* Event Dialog */}
      <EventDialog
        open={eventDialogOpen}
        onOpenChange={setEventDialogOpen}
        event={editingEvent}
        onSave={handleSaveEvent}
        onDelete={editingEvent ? handleDeleteEvent : undefined}
      />
    </div>
  )
}
